import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "app/shadcnComponents/ui/drawer";
import { faUsers, faBullseye } from "@fortawesome/pro-light-svg-icons";
import Button from "app/storybookComponents/Button";
import { faGlobe, faUser, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Separator } from "app/shadcnComponents/ui/separator";
import { GOAL_DRAWER_WIDTH } from "../../constants";
import { useEffect, useMemo, useState } from "react";
import { DrawerSearchInput } from "./DrawerSearchInput";
import { useGetLinkableGoalsQuery } from "../../goalsApi";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { cn } from "app/lib/utils";

interface Props {
  effectiveUserId?: number;
  selectedParentGoalId?: number | null;
  onSelectParentGoal: (parentGoalId: number | null) => void;
}

const ParentGoalSelectorDrawer = ({
  effectiveUserId,
  selectedParentGoalId,
  onSelectParentGoal,
}: Props) => {
  // ---------------------- Redux Selectors ----------------------
  const { data: linkableGoals = [] } = useGetLinkableGoalsQuery(
    effectiveUserId ? { userAccountId: effectiveUserId } : undefined
  );

  // ---------------------- States ----------------------
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedParentGoalState, setSelectedParentGoalState] = useState(
    selectedParentGoalId ?? null
  );

  // ---------------------- Effects ----------------------
  useEffect(() => {
    setSelectedParentGoalState(selectedParentGoalId ?? null);
  }, [selectedParentGoalId]);

  // ---------------------- Memoized Values ----------------------
  const selectedParentGoal = useMemo(
    () => linkableGoals.find((g) => g.goalId === selectedParentGoalId),
    [linkableGoals, selectedParentGoalId]
  );

  const filteredGoals = useMemo(() => {
    if (!search.trim()) return linkableGoals;
    const normalize = (v?: string) =>
      v?.toLowerCase().replaceAll(/\s+/g, " ").trim() ?? "";
    const q = normalize(search);

    return linkableGoals.filter(
      (g) =>
        normalize(g.title).includes(q) ||
        normalize(g.ownerName).includes(q) ||
        normalize(g.ownerRole).includes(q)
    );
  }, [search, linkableGoals]);

  const managerGoals = useMemo(
    () => filteredGoals.filter((g) => g.source === "manager"),
    [filteredGoals]
  );

  const teamGoals = useMemo(
    () => filteredGoals.filter((g) => g.source === "team"),
    [filteredGoals]
  );

  const orgGoals = useMemo(
    () => filteredGoals.filter((g) => g.source === "org"),
    [filteredGoals]
  );

  // ---------------------- Handlers ----------------------
  const onSave = () => {
    onSelectParentGoal(selectedParentGoalState);
    setIsOpen(false);
  };

  // ---------------------- Renders ----------------------
  const getSelectionOption = (
    goalId: number,
    title: string,
    description?: string,
    onClick?: () => void
  ) => {
    const isSelected = goalId === selectedParentGoalState;
    const selectorClassName = cn(
      "text-left d-flex gap-2 w-full px-3 py-2 hover:bg-[var(--surface-2)]",
      isSelected && "bg-[var(--interactive-bg)]"
    );

    return (
      <button onClick={onClick} className={selectorClassName} key={goalId}>
        <input
          type="radio"
          name="goal"
          checked={isSelected}
          onChange={() => {}}
        />
        <div className="d-flex flex-col align-items-start">
          <p className="text-[color:var(--text-primary)] text-[length:var(--fontSize-label)]">
            {title}
          </p>
          <p className="text-[color:var(--text-secondary)] text-[length:var(--fontSize-micro)]">
            {description}
          </p>
        </div>
      </button>
    );
  };

  const getTeamGoals = () => {
    if (teamGoals.length === 0) return null;
    return (
      <div>
        <div className={labelClassName}>
          <FontAwesomeIcon icon={faUsers} />
          <span>Team Goals</span>
        </div>
        {teamGoals.map((goal) => {
          return getSelectionOption(
            goal.goalId,
            goal.title,
            goal.ownerName,
            () => {
              setSelectedParentGoalState(goal.goalId);
            }
          );
        })}
      </div>
    );
  };

  const getOrganizationGoals = () => {
    if (orgGoals.length === 0) return null;
    return (
      <div>
        <div className={labelClassName}>
          <FontAwesomeIcon icon={faGlobe} />
          <span>Organization Goals</span>
        </div>
        {orgGoals.map((goal) => {
          return getSelectionOption(
            goal.goalId,
            goal.title,
            goal.ownerName,
            () => {
              setSelectedParentGoalState(goal.goalId);
            }
          );
        })}
      </div>
    );
  };

  const getManagerGoals = () => {
    if (managerGoals.length === 0) return null;
    return (
      <div>
        <div className={labelClassName}>
          <FontAwesomeIcon icon={faUser} />
          <span>Manager Goals</span>
        </div>
        {managerGoals.map((goal) => {
          return getSelectionOption(
            goal.goalId,
            goal.title,
            goal.ownerName,
            () => {
              setSelectedParentGoalState(goal.goalId);
            }
          );
        })}
      </div>
    );
  };

  const getSelectedParentGoal = () => {
    if (!selectedParentGoal) {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className="rounded border border-input bg-surface-1 px-[12px] py-[10px] text-[var(--text-placeholder)] d-flex gap-sm align-items-center hover:bg-surface-2 hover:border-strong w-full"
        >
          <FontAwesomeIcon icon={faBullseye} />
          <p>Search all shared goals...</p>
          <FontAwesomeIcon icon={faChevronRight} className="ms-auto" />
        </button>
      );
    }

    return (
      <div className="rounded d-flex align-items-center gap-[10px] border-[1px] border-interactive bg-interactive-bg justify-between px-[12px] py-[10px]">
        <div className="d-flex align-items-center gap-[10px]">
          <FontAwesomeIcon
            icon={faBullseye}
            className="text-xl text-interactive"
          />
          <div>
            <strong className="text-label">{selectedParentGoal.title}</strong>
            <div className="muted text-micro">
              {selectedParentGoal.ownerName}
              {selectedParentGoal.ownerRole &&
                ` · ${selectedParentGoal.ownerRole}`}
            </div>
          </div>
        </div>

        <Button
          type="button"
          onClick={() => onSelectParentGoal(null)}
          variant="secondary-blue"
          className="border-0"
        >
          <FontAwesomeIcon icon={faXmark} />
        </Button>
      </div>
    );
  };

  const getEmptyState = () => {
    if (!linkableGoals.length) {
      return (
        <div className="d-flex justify-content-center align-items-center h-full">
          <p className="text-[var(--text-secondary)]">
            No linkable goals found
          </p>
        </div>
      );
    }
    if (!filteredGoals.length) {
      return (
        <div className="d-flex justify-content-center align-items-center h-full">
          <p className="text-[var(--text-secondary)]">
            No linkable goals matching "{search}"
          </p>
        </div>
      );
    }
    return null;
  };

  // ---------------------- Constants ----------------------
  const labelClassName =
    "text-[var(--text-label)] p-[12px_20px_4px] w-full d-flex gap-2 text-[length:var(--fontSize-label)] align-items-center";

  return (
    <>
      <div>
        <p className="text-[length:var(--fontSize-form-prompt)] font-medium text-primary-text mb-sm">
          Link to parent goal (optional)
        </p>
        {getSelectedParentGoal()}
      </div>
      <Drawer direction="right" onOpenChange={setIsOpen} open={isOpen}>
        <DrawerContent
          overlayClassName="bg-black/40"
          className={`right-0 top-0 left-auto bottom-auto mt-0 h-full ${GOAL_DRAWER_WIDTH} rounded-l-[10px] rounded-t-none figtree-container`}
        >
          <DrawerHeader className="d-flex justify-content-between align-items-center border-b pb-4">
            <div>
              <DrawerTitle>Link to parent goal</DrawerTitle>
              <DrawerDescription className="d-none">
                Select a goal from your manager, team, or organization to link
                as the parent goal
              </DrawerDescription>
            </div>
            <DrawerClose asChild>
              <Button variant="secondary-blue">
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </DrawerClose>
          </DrawerHeader>
          <div className="px-3 py-2">
            <DrawerSearchInput
              searchValue={search}
              setSearchValue={setSearch}
            />
          </div>
          <Separator />
          <div className="no-scrollbar overflow-y-auto">
            {getManagerGoals()}
            {getTeamGoals()}
            {getOrganizationGoals()}
          </div>
          {getEmptyState()}
          <Separator className="mt-auto" />
          <DrawerFooter className="d-flex justify-content-between flex-row mt-0">
            <div>
              <Button
                onClick={() => onSelectParentGoal(null)}
                variant="secondary-gray"
                className="border-0"
              >
                Clear selection
              </Button>
            </div>
            <div className="d-flex gap-sm">
              <Button onClick={() => setIsOpen(false)} variant="secondary-gray">
                Cancel
              </Button>
              <Button onClick={onSave}>Submit</Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ParentGoalSelectorDrawer;
